import { useEffect, useContext } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  invalidateSession,
  isUserLoggedIn,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { useReactivation } from '@ltvco/refresh-lib/payments';

interface AuthenticatedRouteProps {
  children: JSX.Element;
}

export function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  const {
    session: { isAuthenticated, account },
    setSession,
  } = useSession(); // should be replaced by the user_info object
  const queryClient = useQueryClient();
  const isNonCompliant = account?.account.user_info.noncompliant;
  const { routingUtils } = useContext(AppConfig);
  const { location: locationAppConfig } = routingUtils;

  const location = useLocation();
  const navigate = useNavigate();

  const complianceRoutes = [
    '/compliance-form',
    '/noncompliance',
    '/compliance-prohibited',
    '/compliance-permitted',
    '/compliance-recomply',
  ];

  useEffect(() => {
    if (!account?.account) return;
    if (
      import.meta.env.VITE_TARGET_ENV &&
      !['production', 'staging'].includes(import.meta.env.VITE_TARGET_ENV)
    )
      return;

    // handleOnboardingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.account]);

  useEffect(() => {
    handleFreshnessHubUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  function handleFreshnessHubUsers() {
    if (!account) return;
    if (account?.account?.staff_info?.staff) return;
    if (
      account?.account.user_info?.design_name === 'freshness' ||
      account?.account.user_info?.design_version === 'v2'
    ) {
      window.location.href = 'https://www.bumper.com/app/';
    }
  }

  useReactivation({ currentPathName: location.pathname });

  function handleOnboardingUsers() {
    // We should redirect all users who have not completed the DQS onboarding
    if (!account?.account) return;
    const sessionOnboardingCompleted = sessionStorage.getItem('dqs_completed');
    if (
      account.account.user_settings.dqs_completed ||
      (sessionOnboardingCompleted && JSON.parse(sessionOnboardingCompleted))
    )
      return;
    const url = location.pathname + location.search + location.hash;
    // Sending the next url to the onboarding page on the
    // location of the AppConfig because when I was sending the values
    // through navigate they weren't being updated with the current path
    // which was causing the user not getting redirected to the correct report generation
    locationAppConfig.state = { next: url };
    navigate('/onboarding');
  }

  useReactivation({ currentPathName: location.pathname });

  if (!isUserLoggedIn()) {
    invalidateSession();
    const url = location.pathname + location.search + location.hash;
    queryClient.clear();

    setTimeout(() => {
      window.location.reload();
    }, 500);

    return <Navigate to="/login" state={{ next: url }} replace />;
  }
  if (isNonCompliant && !complianceRoutes.includes(location.pathname)) {
    return (
      <Navigate to={`/noncompliance`} state={{ from: location }} replace />
    );
  }

  return children;
}
